import { getOwner } from '@ember/application';

/**
 * Creates an optimized model hook for routes using GraphQL
 * @param {Object} route - The route instance
 * @param {Function|Object} queryConfig - Query configuration or function
 * @param {Object} options - Optimization options
 * @returns {Promise<any>} Model data
 */
export async function createOptimizedModel(route, queryConfig, options = {}) {
  const owner = getOwner(route);
  const graphqlOptimizer = owner.lookup('service:graphql-optimizer');

  const {
    cacheTtl = graphqlOptimizer.CACHE_TTL.medium,
    fallbackValue = [],
    transformFn = null,
    listQuery = false,
    paginationParams = {},
  } = options;

  let query,
    variables = {};

  if (typeof queryConfig === 'function') {
    const result = queryConfig();
    query = result.query || result;
    variables = result.variables || {};
  } else {
    query = queryConfig;
  }

  try {
    // Optimize the query
    const optimizedConfig = graphqlOptimizer.optimizeQuery(
      query.loc.source.body,
      variables,
      {
        cacheTtl,
        includeFragments: true,
        ...(listQuery && {
          excludeBody: true,
          optimizeImages: true,
          limit: paginationParams.limit || 10,
          skip: paginationParams.skip || 0,
        }),
      },
    );

    // Execute the optimized query
    const result = await graphqlOptimizer.executeOptimizedQuery(
      async () => {
        if (route.query?.promise) {
          await route.query.promise;
          return route.query.data;
        } else {
          // Fallback for direct query execution
          throw new Error(
            'Query execution not implemented for this route type',
          );
        }
      },
      optimizedConfig,
      route,
    );

    // Transform result if needed
    return transformFn ? transformFn(result) : result;
  } catch (error) {
    console.error('Optimized GraphQL model failed:', error);

    // Handle specific error types
    if (error.status === 404) {
      route.router?.replaceWith('404');
      return null;
    }

    return fallbackValue;
  }
}

/**
 * Creates pagination helpers for list routes
 * @param {Object} route - The route instance
 * @param {Object} baseQuery - Base GraphQL query
 * @returns {Object} Pagination helpers
 */
export function createPaginationHelpers(route, baseQuery) {
  return {
    /**
     * Loads a specific page
     * @param {number} page - Page number (0-based)
     * @param {number} pageSize - Items per page
     * @returns {Promise<any>} Page data
     */
    async loadPage(page = 0, pageSize = 10) {
      const skip = page * pageSize;
      const variables = { limit: pageSize, skip };

      return createOptimizedModel(
        route,
        () => ({ query: baseQuery, variables }),
        {
          listQuery: true,
          paginationParams: { limit: pageSize, skip },
          cacheTtl: route.graphqlOptimizer?.CACHE_TTL.short || 5 * 60 * 1000,
        },
      );
    },

    /**
     * Loads more items for infinite scroll
     * @param {number} currentCount - Current number of items
     * @param {number} loadSize - Number of items to load
     * @returns {Promise<any>} Additional items
     */
    async loadMore(currentCount, loadSize = 10) {
      return this.loadPage(Math.floor(currentCount / loadSize), loadSize);
    },

    /**
     * Refreshes the current page
     * @param {number} page - Current page
     * @param {number} pageSize - Items per page
     * @returns {Promise<any>} Refreshed page data
     */
    async refreshPage(page = 0, pageSize = 10) {
      const owner = getOwner(route);
      const graphqlOptimizer = owner.lookup('service:graphql-optimizer');

      // Clear relevant cache entries
      const skip = page * pageSize;
      const variables = { limit: pageSize, skip };
      const cacheKey = graphqlOptimizer.generateCacheKey(
        baseQuery.loc.source.body,
        variables,
      );
      graphqlOptimizer.queryCache.delete(cacheKey);

      // Load fresh data
      return this.loadPage(page, pageSize);
    },
  };
}

/**
 * Creates a smart query executor that handles caching, retries, and optimization automatically
 * @param {Object} context - Route or component context
 * @param {Object} queryConfig - Query configuration
 * @param {Object} options - Execution options
 * @returns {Promise<any>} Query result
 */
export async function executeSmartQuery(context, queryConfig, options = {}) {
  const owner = getOwner(context);
  const graphqlOptimizer = owner.lookup('service:graphql-optimizer');
  const networkRetry = owner.lookup('service:network-retry');

  const {
    maxRetries = 2,
    cacheTtl = graphqlOptimizer.CACHE_TTL.medium,
    priority = 'normal',
    optimizeForList = false,
    enableBatching = false,
  } = options;

  const { query, variables = {} } = queryConfig;

  // Generate cache key
  const cacheKey = graphqlOptimizer.generateCacheKey(
    query.loc?.source?.body || query,
    variables,
  );

  // Check cache first
  if (graphqlOptimizer.isQueryCached(cacheKey, cacheTtl)) {
    const cached = graphqlOptimizer.getCachedQuery(cacheKey);
    return cached.data;
  }

  // Execute with retry logic
  try {
    const result = await networkRetry.withRetry(
      async () => {
        // Execute the actual GraphQL query
        if (context.query?.promise) {
          await context.query.promise;
          return context.query.data;
        } else {
          throw new Error('Query execution method not available');
        }
      },
      {
        maxRetries,
        baseDelay: 500,
        onRetry: (error, attempt, delay) => {
          console.warn(
            `Smart query retry ${attempt} for ${cacheKey}:`,
            error.message,
          );
        },
      },
    );

    // Cache the result
    graphqlOptimizer.cacheQueryResult(cacheKey, result, cacheTtl);

    return result;
  } catch (error) {
    console.error('Smart query execution failed:', error);
    throw error;
  }
}

/**
 * Creates a query preloader for anticipated navigation
 * @param {Object} context - Route or component context
 * @param {Array} queries - Array of query configurations to preload
 * @returns {Promise<void>} Preloading completion
 */
export async function preloadQueries(context, queries) {
  const owner = getOwner(context);
  const graphqlOptimizer = owner.lookup('service:graphql-optimizer');

  const preloadPromises = queries.map(async (queryConfig) => {
    try {
      await executeSmartQuery(context, queryConfig, {
        cacheTtl: graphqlOptimizer.CACHE_TTL.long,
        priority: 'low',
      });
    } catch (error) {
      // Don't fail preloading if one query fails
      console.warn('Query preload failed:', error.message);
    }
  });

  await Promise.allSettled(preloadPromises);
}

/**
 * Creates a query invalidator for cache management
 * @param {Object} context - Route or component context
 * @returns {Object} Cache invalidation helpers
 */
export function createCacheInvalidator(context) {
  const owner = getOwner(context);
  const graphqlOptimizer = owner.lookup('service:graphql-optimizer');

  return {
    /**
     * Invalidates queries matching a pattern
     * @param {string|RegExp} pattern - Cache key pattern to match
     */
    invalidatePattern(pattern) {
      const regex =
        typeof pattern === 'string'
          ? new RegExp(pattern.replace(/\*/g, '.*'))
          : pattern;

      for (const key of graphqlOptimizer.queryCache.keys()) {
        if (regex.test(key)) {
          graphqlOptimizer.queryCache.delete(key);
        }
      }
    },

    /**
     * Invalidates all queries for a specific content type
     * @param {string} contentType - Content type to invalidate
     */
    invalidateContentType(contentType) {
      this.invalidatePattern(`*${contentType}*`);
    },

    /**
     * Invalidates all list queries
     */
    invalidateListQueries() {
      this.invalidatePattern('*Collection*');
    },

    /**
     * Invalidates a specific query
     * @param {Object} queryConfig - Query configuration
     */
    invalidateQuery(queryConfig) {
      const { query, variables = {} } = queryConfig;
      const cacheKey = graphqlOptimizer.generateCacheKey(
        query.loc?.source?.body || query,
        variables,
      );
      graphqlOptimizer.queryCache.delete(cacheKey);
    },
  };
}
